// @file Utility for managing viz-specific background preferences in localStorage
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { safeLocalStorage } from '@@/bits/safe_storage'
import type { WallBackground, WallViz } from '@@/types'

const STORAGE_KEY = 'padlet_viz_backgrounds'

/**
 * Structure for storing viz background data in localStorage.
 * Organized by wall hashid with a single background entry per wall.
 * Only used for non-map viz layouts.
 *
 * @typedef {Object} VizBackgroundMapping
 * @property {Object.<string, VizBackgroundEntry>} [wallHashid] - Wall-specific background data
 */
interface VizBackgroundMapping {
  [wallHashid: string]: {
    /** The complete background object */
    background: WallBackground
    /** Timestamp when this background was last used/saved */
    lastUsed: number
  }
}

/**
 * Class for managing viz-specific background preferences in storage.
 *
 * This class provides a clean API for storing and retrieving background objects
 * for non-map wall layouts with automatic cleanup.
 */
class VizBackgroundStorage {
  /**
   * Safely retrieves and parses viz background data from storage.
   *
   * @returns {VizBackgroundMapping} Parsed background data or empty object on error
   */
  private getStoredData(): VizBackgroundMapping {
    try {
      const stored = safeLocalStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.warn('Failed to parse viz background storage data:', error)
      return {}
    }
  }

  /**
   * Safely stores viz background data to storage.
   *
   * @param {VizBackgroundMapping} data - The background data to store
   */
  private setStoredData(data: VizBackgroundMapping): void {
    try {
      const success = safeLocalStorage.setItem(STORAGE_KEY, asciiSafeStringify(data))
      if (!success) {
        console.warn('Failed to save viz background storage data')
      }
    } catch (error) {
      console.warn('Failed to save viz background storage data:', error)
    }
  }

  /**
   * Removes background entries that are older than 30 days.
   */
  private cleanupOldEntries(): void {
    const data = this.getStoredData()
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
    let hasChanges = false

    const cleanedData: VizBackgroundMapping = {}

    Object.entries(data).forEach(([wallHashid, entry]) => {
      if (entry != null && typeof entry === 'object' && entry.lastUsed >= thirtyDaysAgo) {
        // Keep recent entries
        cleanedData[wallHashid] = entry
      } else {
        // Mark for removal
        hasChanges = true
      }
    })

    // Only update storage if we made changes
    if (hasChanges) {
      this.setStoredData(cleanedData)
    }
  }

  /**
   * Saves a background object for a specific wall.
   * Only saves if the viz type is not 'map' since map layouts handle backgrounds differently.
   *
   * @param {string} wallHashid - The unique identifier for the wall
   * @param {WallViz} viz - The wall visualization type
   * @param {WallBackground} background - The background object to save
   */
  saveBackgroundForViz(wallHashid: string, viz: WallViz, background: WallBackground): void {
    if (!wallHashid || !viz || !background?.url) return
    if (viz === 'map') return // Don't store backgrounds for map layouts

    this.cleanupOldEntries()
    const data = this.getStoredData()

    data[wallHashid] = {
      background,
      lastUsed: Date.now(),
    }

    this.setStoredData(data)
  }

  /**
   * Retrieves a saved background object for a specific wall.
   * Only returns backgrounds for non-map viz types.
   *
   * @param {string} wallHashid - The unique identifier for the wall
   * @param {WallViz} viz - The wall visualization type
   * @returns {WallBackground|null} The saved background object, or null if not found or viz is 'map'
   */
  getBackgroundForViz(wallHashid: string, viz: WallViz): WallBackground | null {
    if (!wallHashid || !viz) return null
    if (viz === 'map') return null // Don't return backgrounds for map layouts

    // Whenever we are getting a background, we should also clean up old entries so users don't have to manually implement
    this.cleanupOldEntries()
    const data = this.getStoredData()
    const wallData = data[wallHashid]

    return wallData?.background ?? null
  }

  /**
   * Checks if a saved background exists for a specific wall.
   * Only returns true for non-map viz types.
   *
   * @param {string} wallHashid - The unique identifier for the wall
   * @param {WallViz} viz - The wall visualization type
   * @returns {boolean} True if a background exists and viz is not 'map', false otherwise
   */
  hasBackgroundForViz(wallHashid: string, viz: WallViz): boolean {
    return this.getBackgroundForViz(wallHashid, viz) !== null
  }

  /**
   * Removes a saved background for a specific wall.
   * Only removes if the viz type is not 'map'.
   *
   * @param {string} wallHashid - The unique identifier for the wall
   * @param {WallViz} viz - The wall visualization type
   */
  removeBackgroundForViz(wallHashid: string, viz: WallViz): void {
    if (!wallHashid || !viz) return
    if (viz === 'map') return // Don't remove backgrounds for map layouts

    const data = this.getStoredData()

    if (data[wallHashid] !== undefined) {
      const { [wallHashid]: removed, ...remainingData } = data
      this.setStoredData(remainingData)
    }
  }
}

export { VizBackgroundStorage }
